import React from 'react'
import { CheckCircle2, Info, Download, Loader2, ExternalLink, Sparkles, Mic, Zap } from 'lucide-react'
import Button from '../components/ui/button'
import {
  fetchTransactionsForCurrentUser,
  type TransactionDto,
  previewPlanChange,
  changePlan,
  fetchSubscriptionSummary,
  cancelSubscriptionAtPeriodEnd,
  type SubscriptionSummaryResponse,
  downloadInvoice,
  fetchTopUpOptions,
  createTopUpCheckoutSession,
  type TopUpBundle,
  type TopUpKind,
} from '../api/billing'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  LEGACY_PLAN_IDS,
  WEEKLY_PLAN_IDS,
  PLANS,
  type PlanId,
  type EpisodeDurationMinutes,
  WEEKLY_EPISODE_DURATIONS,
  DEFAULT_EPISODE_DURATION_MINUTES,
  isValidEpisodeDuration,
  getPlanCreditsCap,
  getWeeklyPlanPriceLabel,
  isLegacyPlan,
  isWeeklyPlan,
  minutesForCredits,
  creditsForMinutes,
} from '../lib/plans'
import { startCheckoutSession } from '../lib/start-checkout-session'
import PlanChangeDialog from '../components/billing/plan-change-dialog'
import { useConfirm } from '../context/confirm-context'
import { track } from '../lib/analytics/posthog'
import { useAuth } from '../hooks/use-auth'
import BillingFAQ from '../components/billing/billing-faq'

const PLAN_DISPLAY_NAME_BY_ID: Record<PlanId, string> = {
  free: 'Free',
  tier_one: 'Starter',
  tier_two: 'Pro',
  creator: 'Creator',
  tier_three: 'Studio',
  sip: 'Sip',
  brew: 'Brew',
  daily: 'Daily',
}
const getPlanDisplayName = (id: PlanId) => PLAN_DISPLAY_NAME_BY_ID[id] ?? id

const legacyTiers = LEGACY_PLAN_IDS.map((id) => PLANS[id])
const weeklyTiers = WEEKLY_PLAN_IDS.map((id) => PLANS[id])

const formatDate = (iso: string) => new Date(iso).toLocaleString()

const getButtonText = (currentPlan: PlanId, targetPlan: PlanId): string => {
  if (currentPlan === targetPlan) return 'Current plan'

  const planHierarchy: PlanId[] =
    isLegacyPlan(currentPlan) || isLegacyPlan(targetPlan)
      ? ['free', ...LEGACY_PLAN_IDS]
      : ['free', ...WEEKLY_PLAN_IDS]
  const currentIndex = planHierarchy.indexOf(currentPlan)
  const targetIndex = planHierarchy.indexOf(targetPlan)

  if (currentPlan === 'free' && targetPlan !== 'free') return 'Subscribe'
  if (currentIndex < targetIndex) return 'Upgrade'
  if (currentIndex > targetIndex) return 'Downgrade'
  return 'Subscribe'
}

const BillingPage: React.FC = () => {
  const { user } = useAuth()
  const { confirm } = useConfirm()
  const queryClient = useQueryClient()

  React.useEffect(() => {
    localStorage.setItem('has_seen_billing', 'true')
  }, [])

  const { data: transactions = [] } = useQuery({
    queryKey: ['billing', 'transactions'],
    queryFn: fetchTransactionsForCurrentUser,
    staleTime: 60 * 1000,
  })

  const { data: subSummary } = useQuery<SubscriptionSummaryResponse>({
    queryKey: ['billing', 'subscription-summary'],
    queryFn: fetchSubscriptionSummary,
    staleTime: 30 * 1000,
  })

  const { data: topUpData } = useQuery({
    queryKey: ['billing', 'topup-options'],
    queryFn: fetchTopUpOptions,
    staleTime: 60 * 1000,
  })

  const [topUpLoadingKind, setTopUpLoadingKind] = React.useState<string | null>(null)

  const [success, setSuccess] = React.useState<string | null>(null)
  const [topUpSuccess, setTopUpSuccess] = React.useState<{ kind: TopUpKind; minutes?: number } | null>(null)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const sessionId = params.get('session_id')
    if (sessionId) {
      setSuccess(sessionId)
      // The webhook that flips the user's plan can land a moment after the
      // checkout redirect. Poll the user/summary queries for a few seconds so
      // the UI reflects the new plan without requiring a manual refresh.
      let attempts = 0
      const tick = async () => {
        attempts += 1
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
        await queryClient.invalidateQueries({ queryKey: ['billing', 'subscription-summary'] })
        if (attempts < 8) {
          window.setTimeout(tick, 1500)
        }
      }
      tick()
    }

    const topUpSuccessParam = params.get('topup_success')
    const kind = (params.get('kind') ?? 'minutes') as TopUpKind
    if (topUpSuccessParam === 'true') {
      const minutesParam = params.get('minutes')
      setTopUpSuccess({ kind, minutes: minutesParam ? parseInt(minutesParam, 10) : undefined })
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription-summary'] })
    }
  }, [queryClient])

  const [couponCode, setCouponCode] = React.useState<string | null>(null)
  const [trialFromQuery, setTrialFromQuery] = React.useState(false)
  const [paywallSource, setPaywallSource] = React.useState<string | null>(null)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const coupon = params.get('coupon') || params.get('promo') || params.get('code')
    if (coupon) setCouponCode(coupon)

    if (params.get('trial') === '1' || params.get('trial') === 'true') setTrialFromQuery(true)

    const from = params.get('from')
    if (from) setPaywallSource(from)
  }, [])

  const [planDialogOpen, setPlanDialogOpen] = React.useState(false)
  const [planDialogLoading, setPlanDialogLoading] = React.useState(false)
  const [planDialogPreview, setPlanDialogPreview] =
    React.useState<import('../api/billing').PreviewPlanChangeResponse | null>(null)
  const [targetPlan, setTargetPlan] = React.useState<PlanId | null>(null)

  const [checkoutLoading, setCheckoutLoading] = React.useState<PlanId | null>(null)

  const currentPlan = ((user?.plan && PLANS[user.plan as PlanId]) ? user.plan : 'free') as PlanId
  const showLegacyPricing = isLegacyPlan(currentPlan)

  // Currently-active episode duration for the user (only meaningful on weekly plans).
  const userEpisodeDuration: EpisodeDurationMinutes = isValidEpisodeDuration(user?.episodeDurationMinutes)
    ? user!.episodeDurationMinutes as EpisodeDurationMinutes
    : DEFAULT_EPISODE_DURATION_MINUTES

  // Episode-duration variant currently being previewed. Defaults to the user's current duration.
  const [selectedDuration, setSelectedDuration] = React.useState<EpisodeDurationMinutes>(userEpisodeDuration)
  React.useEffect(() => {
    if (isValidEpisodeDuration(user?.episodeDurationMinutes)) {
      setSelectedDuration(user!.episodeDurationMinutes as EpisodeDurationMinutes)
    }
  }, [user?.episodeDurationMinutes])

  // Default-selected plan: keep current if paid; otherwise pre-select brew (recommended).
  const defaultSelected: PlanId = currentPlan === 'free' ? 'brew' : currentPlan
  const [selectedPlanId, setSelectedPlanId] = React.useState<PlanId>(defaultSelected)
  React.useEffect(() => {
    if (currentPlan !== 'free') {
      setSelectedPlanId(currentPlan)
    } else if (!showLegacyPricing && isLegacyPlan(selectedPlanId)) {
      setSelectedPlanId('brew')
    }
  }, [currentPlan, selectedPlanId, showLegacyPricing])

  const handleSelectPlan = async (plan: PlanId) => {
    try {
      if (currentPlan === 'free' && plan !== 'free') {
        setCheckoutLoading(plan)
        await startCheckoutSession(plan, {
          fromPlan: currentPlan,
          couponCode,
          trial: trialFromQuery,
          episodeDurationMinutes: isWeeklyPlan(plan) ? selectedDuration : undefined,
        })
        return
      }
      // Same plan + same duration = nothing to do.
      const sameDuration = !isWeeklyPlan(plan) || selectedDuration === userEpisodeDuration
      if (plan === currentPlan && sameDuration) return

      setTargetPlan(plan)
      setPlanDialogOpen(true)
      setPlanDialogLoading(true)
      try {
        const preview = await previewPlanChange(plan, isWeeklyPlan(plan) ? selectedDuration : undefined)
        setPlanDialogPreview(preview)
      } catch (e) {
        console.error(e)
        setPlanDialogOpen(false)
        alert('Unable to load plan change preview. Please try again.')
        track('billing_error', {
          action: 'preview_plan_change',
          fromPlan: currentPlan,
          toPlan: plan,
          error: (e as Error)?.message,
        })
        return
      } finally {
        setPlanDialogLoading(false)
      }
    } catch (e) {
      console.error(e)
      alert('Unable to process billing action. Please try again.')
      track('billing_error', { action: 'select_plan', plan, error: (e as Error)?.message })
      setCheckoutLoading(null)
    }
  }

  const handleConfirmPlanChange = async () => {
    if (!targetPlan) return
    setPlanDialogLoading(true)
    const oldPlan = currentPlan
    try {
      const res = await changePlan(targetPlan, isWeeklyPlan(targetPlan) ? selectedDuration : undefined)
      if (res.success) {
        setPlanDialogOpen(false)
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
        await queryClient.invalidateQueries({ queryKey: ['billing', 'subscription-summary'] })
        track('billing_plan_change_completed', {
          fromPlan: oldPlan,
          toPlan: targetPlan,
          appliedImmediately: res.appliedImmediately,
        })
        alert(res.appliedImmediately ? 'Plan upgraded.' : 'Downgrade scheduled for next cycle.')
      }
    } catch (e) {
      console.error(e)
      track('billing_error', { action: 'change_plan', fromPlan: oldPlan, toPlan: targetPlan, error: (e as Error)?.message })
      alert('Unable to change plan. Please try again.')
    } finally {
      setPlanDialogLoading(false)
    }
  }

  const handleCancelAtPeriodEnd = async () => {
    const confirmed = await confirm({
      title: 'Cancel your subscription?',
      description:
        "Your subscription will be cancelled at the end of your current billing period. You'll keep access to all features until then, but won't be charged again.",
      confirmText: 'Yes, cancel subscription',
      cancelText: 'Keep my subscription',
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      const res = await cancelSubscriptionAtPeriodEnd()
      if (res?.cancelAtPeriodEnd) {
        await queryClient.invalidateQueries({ queryKey: ['billing', 'subscription-summary'] })
        track('billing_subscription_cancel_requested', { plan: currentPlan })
        alert('Subscription will cancel at the end of the current period.')
      }
    } catch (e) {
      console.error(e)
      track('billing_error', { action: 'cancel_subscription', plan: currentPlan, error: (e as Error)?.message })
      alert('Unable to schedule cancellation. Please try again.')
    }
  }

  const handleTopUpPurchase = async (bundle: TopUpBundle) => {
    const loadingKey = `${bundle.kind}-${bundle.minutes || bundle.episodes}`
    setTopUpLoadingKind(loadingKey)
    try {
      const res = await createTopUpCheckoutSession({
        kind: bundle.kind,
        minutes: bundle.kind === 'minutes' ? bundle.minutes : undefined,
      })
      if (res.url) {
        track('billing_topup_checkout_started', {
          kind: bundle.kind,
          minutes: bundle.minutes,
          episodes: bundle.episodes,
          price: bundle.price,
        })
        window.location.href = res.url
      }
    } catch (e) {
      console.error(e)
      track('billing_error', { action: 'topup_checkout', kind: bundle.kind, error: (e as Error)?.message })
      alert('Unable to start checkout. Please try again.')
    } finally {
      setTopUpLoadingKind(null)
    }
  }

  const sliderPlans = showLegacyPricing ? legacyTiers : weeklyTiers
  const selectedPlanInfo = PLANS[selectedPlanId] ?? PLANS.brew

  const episodeBundles = (topUpData?.bundles ?? []).filter((b) => b.kind === 'episode')
  const minuteBundles = (topUpData?.bundles ?? []).filter((b) => b.kind === 'minutes')

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Page header */}
      <header className="space-y-2">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-foreground">
            {paywallSource === 'download_episode'
              ? 'Upgrade to download episodes'
              : paywallSource === 'private_podcasts'
              ? 'Upgrade for private podcasts'
              : paywallSource === 'duration_longer_episodes'
              ? 'Upgrade for longer episodes'
              : 'Billing'}
          </h1>
          {currentPlan !== 'free' && (
            <span className="rounded-full border border-black/10 bg-white px-3 py-1 text-xs text-foreground/70">
              {getPlanDisplayName(currentPlan)} plan
            </span>
          )}
        </div>

        {/* Usage / free trial status */}
        {(() => {
          if (currentPlan === 'free') {
            const FREE_TRIAL_MINUTES = 10
            const FREE_TRIAL_CREDITS = creditsForMinutes(FREE_TRIAL_MINUTES)
            const used = Math.max(0, user?.creditsUsed ?? 0)
            const remainingCredits = Math.max(0, FREE_TRIAL_CREDITS - used)
            const remainingMinutes = minutesForCredits(remainingCredits)
            const seriesUsed = !!user?.freeSeriesEpisodeUsed
            return (
              <p className="text-sm text-foreground/60">
                {seriesUsed ? (
                  <>Your free episode has been used. Subscribe to keep generating.</>
                ) : remainingMinutes > 0 ? (
                  <>
                    You have <span className="text-foreground font-medium">1 free episode</span> available (up to 10 min).
                  </>
                ) : (
                  <>Your free trial is used up. Upgrade to keep generating podcasts.</>
                )}
              </p>
            )
          }

          if (isWeeklyPlan(currentPlan)) {
            const cap = user?.episodesPerCycle ?? PLANS[currentPlan].episodesPerCycle ?? 0
            const usedEp = user?.episodesUsedThisCycle ?? 0
            const bonusEp = user?.bonusEpisodes ?? 0
            const planRemaining = Math.max(0, cap - usedEp)
            const totalRemaining = planRemaining + bonusEp
            return (
              <p className="text-sm text-foreground/60">
                You have <span className="text-foreground font-medium">{totalRemaining}</span> of <span className="font-medium">{cap}</span> episodes left this week
                {bonusEp > 0 && <span className="text-brand-600"> (incl. {bonusEp} bonus)</span>}.
              </p>
            )
          }

          const cap = getPlanCreditsCap(currentPlan)
          const used = Math.min(Math.max(0, user?.creditsUsed ?? 0), cap)
          const planRemaining = Math.max(0, cap - used)
          const bonusCredits = user?.bonusCredits ?? 0
          const totalRemaining = planRemaining + bonusCredits
          const remainingMinutes = minutesForCredits(totalRemaining)
          const bonusMinutes = minutesForCredits(bonusCredits)
          return (
            <p className="text-sm text-foreground/60">
              You have <span className="text-foreground font-medium">{remainingMinutes} min</span> left this cycle
              {bonusMinutes > 0 && <span className="text-brand-600"> (incl. {bonusMinutes} bonus)</span>}.
            </p>
          )
        })()}

        {/* Notices */}
        <div className="flex flex-wrap gap-2 pt-1">
          {trialFromQuery && currentPlan === 'free' && (
            <span className="inline-flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 text-brand-800 px-3 py-1.5 text-sm">
              <Sparkles className="h-4 w-4" />
              Your first week will be just <strong className="font-semibold">$1</strong>.
            </span>
          )}
          {couponCode && (
            <span className="inline-flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 text-brand-800 px-3 py-1.5 text-sm">
              <CheckCircle2 className="h-4 w-4" />
              Promo <span className="font-medium">{couponCode}</span> will be applied at checkout.
            </span>
          )}
          {success && (
            <span className="inline-flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 text-brand-800 px-3 py-1.5 text-sm">
              <CheckCircle2 className="h-4 w-4" />
              Payment successful. Your plan will update shortly.
            </span>
          )}
          {topUpSuccess && (
            <span className="inline-flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 text-brand-800 px-3 py-1.5 text-sm">
              <CheckCircle2 className="h-4 w-4" />
              {topUpSuccess.kind === 'episode'
                ? 'Top-up successful! +1 episode added.'
                : `Top-up successful! ${topUpSuccess.minutes} minutes added to your account.`}
            </span>
          )}
          {subSummary?.pendingPlanDowngrade && (
            <span className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-1.5 text-sm">
              <Info className="h-4 w-4" />
              Downgrade to{' '}
              <strong className="text-amber-900">{getPlanDisplayName(subSummary.pendingPlanDowngrade as PlanId)}</strong>{' '}
              next cycle
              {subSummary.currentPeriodEnd ? ` on ${formatDate(subSummary.currentPeriodEnd)}` : ''}.
            </span>
          )}
          {subSummary?.cancelAtPeriodEnd && (
            <span className="inline-flex items-center gap-2 rounded-lg border border-danger-200 bg-danger-50 text-danger-800 px-3 py-1.5 text-sm">
              <Info className="h-4 w-4" />
              Subscription will cancel at period end
              {subSummary.currentPeriodEnd ? ` on ${formatDate(subSummary.currentPeriodEnd)}` : ''}.
            </span>
          )}
        </div>
      </header>

      {/* Minimal social proof */}
      <section className="rounded-2xl border border-black/10 bg-white p-4 sm:p-5">
        <a
          href="https://www.reddit.com/r/studytips/comments/1n05fb4/comment/narolzb/"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 inline-flex items-center text-foreground/60 hover:text-brand-700 underline decoration-brand-300 underline-offset-4 cursor-pointer"
        >
          <blockquote className="text-sm sm:text-base leading-relaxed text-foreground/75 inline">
            “This is awesome! Podcast-generator.ai is the most in-depth podcast generator I've encountered so far.”
            <ExternalLink className="ml-1 h-3.5 w-3.5 inline" aria-hidden="true" />
          </blockquote>
        </a>
        <div className="mt-3 text-[11px] text-foreground/60">
          <span className="font-medium text-foreground">Trusted by 1000+ creators</span>
          <span className="mx-2 opacity-50">•</span>
          <span>2000+ podcasts generated</span>
        </div>
      </section>

      {/* Plans */}
      <section aria-labelledby="plans-heading" className="space-y-6">
        <div>
          <h2 id="plans-heading" className="text-xl font-semibold text-foreground">
            Choose your plan
          </h2>
          <p className="text-sm text-foreground/60 mt-1">
            {showLegacyPricing
              ? 'Your current subscription uses our original monthly pricing. Upgrade or downgrade at any time.'
              : 'Pick how many episodes per week you want. Choose the episode length that fits you best.'}
          </p>
        </div>

        {!showLegacyPricing && (
          <DurationSelector value={selectedDuration} onChange={setSelectedDuration} />
        )}

        {showLegacyPricing ? (
          <div className="rounded-2xl border border-black/10 bg-white p-5 sm:p-6 shadow-sm flex flex-col gap-6">
            <div>
              <label className="text-sm font-medium text-foreground/80 mb-3 block">Select your plan</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {sliderPlans.map((p) => {
                  const isSelected = selectedPlanId === p.id
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPlanId(p.id)}
                      className={`relative p-3 text-left rounded-xl border transition-all cursor-pointer ${
                        isSelected
                          ? 'border-brand-600 bg-brand-50/50 ring-1 ring-brand-600 shadow-sm'
                          : 'border-black/10 bg-white hover:border-brand-300 hover:bg-brand-50/30'
                      }`}
                    >
                      <div className={`font-semibold text-sm ${isSelected ? 'text-brand-800' : 'text-foreground'}`}>
                        {getPlanDisplayName(p.id)}
                      </div>
                      <div className={`text-xs mt-0.5 ${isSelected ? 'text-brand-600 font-medium' : 'text-foreground/50'}`}>
                        {p.allowanceLabel ?? `${p.minutesPerMonth} mins/mo`}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="border-t border-black/5 pt-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="space-y-1.5">
                <h3 className="text-2xl font-bold text-foreground">
                  {selectedPlanInfo.priceLabel}
                  <span className="text-sm text-foreground/50 font-normal"> / month</span>
                </h3>
                <div className="flex items-center gap-2 text-sm text-foreground/70">
                  <CheckCircle2 className="h-4 w-4 text-brand-500" />
                  {selectedPlanInfo.allowanceLabel}
                </div>
              </div>

              <div className="min-w-[140px]">
                {selectedPlanId === currentPlan ? (
                  <div className="w-full rounded-lg bg-black/5 py-2.5 px-4 text-center text-sm font-medium text-foreground/60">
                    Current plan
                  </div>
                ) : (
                  <Button
                    onClick={() => handleSelectPlan(selectedPlanId)}
                    disabled={checkoutLoading !== null}
                    className="w-full py-2.5 text-sm font-semibold"
                  >
                    {checkoutLoading === selectedPlanId ? (
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    ) : (
                      getButtonText(currentPlan, selectedPlanId)
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {weeklyTiers.map((plan) => {
              const isSelected = selectedPlanId === plan.id
              const isCurrent = plan.id === currentPlan
              const isCurrentSameDuration = isCurrent && selectedDuration === userEpisodeDuration
              const planPriceLabel = getWeeklyPlanPriceLabel(plan.id, selectedDuration)
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border bg-white p-6 shadow-sm flex flex-col transition-all ${
                    plan.recommended
                      ? 'border-brand-600 ring-2 ring-brand-600 ring-offset-2 ring-offset-background'
                      : 'border-black/10'
                  } ${isSelected ? 'shadow-md' : ''}`}
                >
                  {plan.recommended && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
                      Most Popular
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                    {isCurrent && (
                      <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-medium text-foreground/60">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-foreground">{planPriceLabel}</span>
                    <span className="text-sm text-foreground/60">/ week</span>
                  </div>
                  <p className="mt-1 text-sm text-foreground/60">{plan.cadenceLabel}</p>

                  <ul className="mt-5 space-y-2 text-sm text-foreground/75">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>{plan.episodesPerCycle}</strong> episode{plan.episodesPerCycle === 1 ? '' : 's'} per week
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-600 mt-0.5 flex-shrink-0" />
                      <span><strong>{selectedDuration} min</strong> per episode</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-600 mt-0.5 flex-shrink-0" />
                      <span>Deep research included</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-600 mt-0.5 flex-shrink-0" />
                      <span>Downloads & private podcasts</span>
                    </li>
                    {plan.priorityQueue && (
                      <li className="flex items-start gap-2">
                        <Zap className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>Priority generation queue</span>
                      </li>
                    )}
                  </ul>

                  <div className="mt-auto pt-6">
                    {isCurrentSameDuration ? (
                      <div className="w-full rounded-lg bg-black/5 py-2.5 px-4 text-center text-sm font-medium text-foreground/60">
                        Current plan
                      </div>
                    ) : (
                      <Button
                        onClick={() => {
                          setSelectedPlanId(plan.id)
                          handleSelectPlan(plan.id)
                        }}
                        disabled={checkoutLoading !== null}
                        className="w-full py-2.5 text-sm font-semibold"
                      >
                        {checkoutLoading === plan.id ? (
                          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                        ) : isCurrent ? (
                          `Change to ${selectedDuration} min`
                        ) : trialFromQuery && currentPlan === 'free' && plan.id !== 'daily' ? (
                          'Start $1 first week'
                        ) : (
                          getButtonText(currentPlan, plan.id)
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Top-Up Section — only useful once the user has a paid plan */}
      {currentPlan !== 'free' && (
      <section aria-labelledby="topup-heading" className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 id="topup-heading" className="text-base font-semibold text-foreground">
            Add-ons & top-ups
          </h2>
          {((user?.bonusEpisodes ?? 0) > 0 || (user?.bonusCredits ?? 0) > 0) && (
            <p className="text-xs text-brand-600 font-medium flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              {(user?.bonusEpisodes ?? 0) > 0 && <span>{user?.bonusEpisodes} bonus episodes</span>}
              {(user?.bonusEpisodes ?? 0) > 0 && (user?.bonusCredits ?? 0) > 0 && ' • '}
              {(user?.bonusCredits ?? 0) > 0 && (
                <span>{minutesForCredits(user?.bonusCredits ?? 0)} bonus minutes</span>
              )}
            </p>
          )}
        </div>

        {!topUpData ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-foreground/40" />
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-sm text-foreground/70">
              Run out mid-week? Grab a one-off episode top-up. Same quality, same length options, no commitment.
            </p>

            {episodeBundles.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {episodeBundles.map((bundle) => {
                  const key = `${bundle.kind}-${bundle.episodes}`
                  const Icon = Mic
                  return (
                    <div
                      key={key}
                      className="rounded-xl border border-black/10 bg-white p-4 flex flex-col gap-3 hover:border-brand-300 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5 text-brand-600" />
                          <h3 className="font-semibold text-foreground">{bundle.title}</h3>
                        </div>
                        <span className="text-lg font-bold text-foreground">${bundle.price.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-foreground/60 leading-relaxed">{bundle.description}</p>
                      <Button
                        variant="secondary"
                        onClick={() => handleTopUpPurchase(bundle)}
                        disabled={topUpLoadingKind !== null}
                        className="cursor-pointer self-start mt-auto"
                      >
                        {topUpLoadingKind === key ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buy now'}
                      </Button>
                    </div>
                  )
                })}
              </div>
            )}

            {minuteBundles.length > 0 && (
              <div className="border-t border-black/5 pt-5">
                <p className="text-xs uppercase tracking-wide text-foreground/50 mb-3">Minute bundles (legacy)</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {minuteBundles.map((bundle) => {
                    const key = `${bundle.kind}-${bundle.minutes}`
                    return (
                      <button
                        key={key}
                        onClick={() => handleTopUpPurchase(bundle)}
                        disabled={topUpLoadingKind !== null}
                        className="rounded-xl border border-black/10 bg-white p-4 text-left hover:border-brand-300 hover:bg-brand-50/30 transition-all cursor-pointer disabled:opacity-60"
                      >
                        <div className="text-2xl font-semibold text-foreground">
                          {bundle.minutes}
                          <span className="text-base font-normal text-foreground/60 ml-1">min</span>
                        </div>
                        <div className="text-sm text-foreground/70 mt-1">${bundle.price.toFixed(2)}</div>
                        {topUpLoadingKind === key && (
                          <Loader2 className="h-4 w-4 animate-spin text-foreground/60 mt-2" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </section>
      )}

      {/* FAQ Section */}
      <BillingFAQ />

      {/* Payment history (condensed) */}
      <section aria-labelledby="history-heading" className="space-y-2">
        <h2 id="history-heading" className="text-base font-semibold text-foreground">
          Payment history
        </h2>
        {transactions.length === 0 ? (
          <p className="text-sm text-foreground/60">No transactions yet.</p>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-black/10">
            <div className="min-w-full divide-y divide-black/5">
              {transactions.map((tx: TransactionDto) => (
                <div
                  key={tx.id}
                  className="group grid grid-cols-2 md:grid-cols-5 gap-2 items-center px-4 py-3 bg-white hover:bg-black/5 transition-colors"
                >
                  <div className="text-sm font-medium text-foreground">{tx.planType}</div>
                  <div className="hidden md:block text-sm text-foreground/70">{formatDate(tx.transactionDate)}</div>
                  <div className="text-sm text-foreground/70">
                    {tx.currency} {tx.amount.toFixed(2)}
                  </div>
                  <div className="hidden md:block text-xs text-foreground/60">{tx.status}</div>
                  <div className="text-right">
                    {tx.status === 'succeeded' && (
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded-md hover:bg-brand-50 text-brand-700 hover:text-brand-800 cursor-pointer"
                        onClick={() => downloadInvoice(tx.id)}
                        title="Download invoice"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Manage subscription */}
      {currentPlan !== 'free' && (
        <section aria-labelledby="manage-subscription-heading" className="space-y-2">
          <h2 id="manage-subscription-heading" className="sr-only">
            Manage subscription
          </h2>
          <div className="rounded-2xl border border-black/10 bg-white p-4 sm:p-5 flex items-center justify-between">
            <div className="text-sm text-foreground/70">
              <p>Cancel at the end of your current billing period. You'll keep access until then.</p>
              {subSummary?.currentPeriodEnd && (
                <p className="mt-1 text-xs text-foreground/60">
                  Current period ends on {formatDate(subSummary.currentPeriodEnd)}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelAtPeriodEnd}
              disabled={!!subSummary?.cancelAtPeriodEnd}
              className="cursor-pointer"
            >
              {subSummary?.cancelAtPeriodEnd ? 'Cancellation scheduled' : 'Cancel subscription'}
            </Button>
          </div>
        </section>
      )}

      {/* Legal Links */}
      <footer className="text-center pb-8">
        <p className="text-xs text-foreground/40">
          <a
            href="https://podcast-generator.ai/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground/60 underline underline-offset-2"
          >
            Terms of service
          </a>
          {' · '}
          <a
            href="https://podcast-generator.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground/60 underline underline-offset-2"
          >
            Privacy policy
          </a>
        </p>
      </footer>

      {/* Plan change dialog */}
      <PlanChangeDialog
        open={planDialogOpen}
        onOpenChange={setPlanDialogOpen}
        currentPlanName={getPlanDisplayName(currentPlan)}
        targetPlanName={targetPlan ? getPlanDisplayName(targetPlan) : ''}
        preview={planDialogPreview}
        loading={planDialogLoading}
        onConfirm={handleConfirmPlanChange}
      />
    </div>
  )
}

export default BillingPage

interface DurationSelectorProps {
  value: EpisodeDurationMinutes
  onChange: (next: EpisodeDurationMinutes) => void
}

const DurationSelector: React.FC<DurationSelectorProps> = ({ value, onChange }) => {
  const activeIndex = WEEKLY_EPISODE_DURATIONS.indexOf(value)
  const segmentPct = 100 / WEEKLY_EPISODE_DURATIONS.length
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 sm:p-5 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">Episode length</p>
          <p className="text-xs text-foreground/60 mt-0.5">Sets the length of every podcast you generate.</p>
        </div>
        <div className="relative flex w-full sm:w-auto rounded-full bg-black/5 p-1 select-none">
          <span
            aria-hidden
            className="absolute top-1 bottom-1 rounded-full bg-white shadow-sm transition-all duration-200 ease-out"
            style={{ left: `calc(${activeIndex * segmentPct}% + 4px)`, width: `calc(${segmentPct}% - 8px)` }}
          />
          {WEEKLY_EPISODE_DURATIONS.map((duration) => {
            const isActive = duration === value
            return (
              <button
                key={duration}
                type="button"
                onClick={() => onChange(duration)}
                className={`relative z-10 flex-1 sm:flex-none sm:min-w-[88px] px-4 py-1.5 text-sm font-medium rounded-full transition-colors cursor-pointer ${
                  isActive ? 'text-foreground' : 'text-foreground/60 hover:text-foreground'
                }`}
                aria-pressed={isActive}
              >
                {duration} min
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
